/**
 * Minimal markdown renderer for LLM answers.
 *
 * Supports: headings, bold, italic, inline code, links, unordered/ordered
 * lists, and paragraphs. All source text is HTML-escaped before markdown
 * markup is applied, so model output cannot inject HTML.
 */
function renderMarkdown(md) {
    const escape = s => s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const inline = s => s
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>')
        .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+|\/[^)\s]*)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

    const blocks = escape(md.trim()).split(/\n{2,}/);

    return blocks.map(block => {
        const lines = block.split('\n');

        // Headings
        const heading = block.match(/^(#{1,4})\s+(.*)$/);
        if (heading && lines.length === 1) {
            const level = Math.min(heading[1].length + 3, 6);
            return `<h${level}>${inline(heading[2])}</h${level}>`;
        }

        // Lists (every line is a bullet or ordered item)
        if (lines.every(l => /^\s*[-*]\s+/.test(l))) {
            const items = lines.map(l => `<li>${inline(l.replace(/^\s*[-*]\s+/, ''))}</li>`).join('');
            return `<ul>${items}</ul>`;
        }

        if (lines.every(l => /^\s*\d+[.)]\s+/.test(l))) {
            const items = lines.map(l => `<li>${inline(l.replace(/^\s*\d+[.)]\s+/, ''))}</li>`).join('');
            return `<ol>${items}</ol>`;
        }

        // Paragraph (single newlines become <br>)
        return `<p>${inline(lines.join('<br>'))}</p>`;
    }).join('');
}

/**
 * Wraps citation markers like [1] or [2][3] in clickable buttons that
 * link to the corresponding reference tile for the given exchange.
 */
function linkCitationMarkers(container, exchangeId, citationCount) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes = [];

    while (walker.nextNode()) {
        if (/\[\d+\]/.test(walker.currentNode.nodeValue)) textNodes.push(walker.currentNode);
    }

    textNodes.forEach(node => {
        const frag = document.createDocumentFragment();
        const parts = node.nodeValue.split(/(\[\d+\])/);

        parts.forEach(part => {
            const m = part.match(/^\[(\d+)\]$/);
            const n = m ? parseInt(m[1], 10) : 0;

            if (n >= 1 && n <= citationCount) {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'ai-search__citation-marker';
                btn.textContent = `[${n}]`;
                btn.dataset.reference = `${exchangeId}-${n}`;
                frag.appendChild(btn);
            } else {
                frag.appendChild(document.createTextNode(part));
            }
        });

        node.parentNode.replaceChild(frag, node);
    });
}

export default function AiSearch(el, {
    formHandle,
    questionHandle,
    threadHandle,
    referencesHandle,
    referencesEmptyHandle,
    submitHandle,
    closeHandle,
    launcherHandle,
    scrimHandle,
    activeClass,
    endpoint,
    csrfTokenName,
    actions,
    events,
}) {
    const form = el.querySelector(formHandle);
    const question = el.querySelector(questionHandle);
    const thread = el.querySelector(threadHandle);
    const references = el.querySelector(referencesHandle);
    const referencesEmpty = el.querySelector(referencesEmptyHandle);
    const submit = el.querySelector(submitHandle);
    const close = el.querySelector(closeHandle);
    const launcher = el.querySelector(launcherHandle);
    const scrim = el.querySelector(scrimHandle);
    const csrfToken = form.querySelector(`[name="${csrfTokenName}"]`).value;

    let busy = false;
    let exchangeCount = 0;

    // Event handler functions
    function handleKeyup(e) {
        if (e.keyCode !== 27) return;

        events.emit(actions.closeAiSearch);
    }

    function handleOpen() {
        events.emit(actions.lockScroll);
        el.classList.add(activeClass);
        question.focus();

        document.addEventListener('keyup', handleKeyup);
    }

    function handleClose() {
        events.emit(actions.unlockScroll);
        el.classList.remove(activeClass);

        document.removeEventListener('keyup', handleKeyup);
    }

    function handleAsk(e) {
        const { q } = e.detail;

        question.value = q;
        form.dispatchEvent(new Event('submit', { cancelable: true }));
        events.emit(actions.openAiSearch);
    }

    function scrollThread() {
        thread.scrollTop = thread.scrollHeight;
    }

    function addMessage(role, options = {}) {
        const message = document.createElement('div');
        message.className = `ai-search__message ai-search__message--${role}`;

        const content = document.createElement('div');
        content.className = 'ai-search__message-content';

        if (options.text !== undefined) content.textContent = options.text;
        if (options.markdown !== undefined) content.innerHTML = renderMarkdown(options.markdown);
        if (options.pending) message.classList.add('is-pending');

        message.appendChild(content);
        thread.appendChild(message);
        scrollThread();

        return message;
    }

    function buildReferenceTile(citation, exchangeId) {
        const li = document.createElement('li');
        li.className = 'ai-search__reference';
        li.dataset.reference = `${exchangeId}-${citation.source}`;

        const head = document.createElement('div');
        head.className = 'ai-search__reference-head';

        const number = document.createElement('span');
        number.className = 'ai-search__reference-number';
        number.textContent = `[${citation.source}]`;

        const type = document.createElement('span');
        type.className = `ai-search__reference-type ai-search__reference-type--${citation.sourceType}`;
        type.textContent = citation.sourceType === 'asset' ? 'Document' : 'Page';

        head.append(number, type);

        const label = (citation.title || citation.filename || 'Untitled')
            + (citation.pageNumber ? `, page ${citation.pageNumber}` : '');

        let title;
        if (citation.url) {
            title = document.createElement('a');
            title.href = citation.url;
            title.target = '_blank';
            title.rel = 'noopener';
            title.textContent = label;
        } else {
            title = document.createElement('span');
            title.textContent = label;
        }
        title.className = 'ai-search__reference-title';

        const snippet = document.createElement('p');
        snippet.className = 'ai-search__reference-snippet';
        snippet.textContent = citation.snippet;

        li.append(head, title, snippet);

        return li;
    }

    function addReferenceGroup(citations, exchangeId, questionText) {
        if (!citations.length) return;

        referencesEmpty.hidden = true;

        const group = document.createElement('li');
        group.className = 'ai-search__reference-group';

        const heading = document.createElement('h4');
        heading.className = 'ai-search__reference-group-heading';
        heading.textContent = questionText;

        const list = document.createElement('ol');
        list.className = 'ai-search__reference-group-list';
        citations.forEach(c => list.appendChild(buildReferenceTile(c, exchangeId)));

        group.append(heading, list);

        // Newest exchange on top
        references.prepend(group);
        references.parentNode.scrollTop = 0;
    }

    function highlightReference(key) {
        const tile = references.querySelector(`[data-reference="${key}"]`);
        if (!tile) return;

        tile.scrollIntoView({ behavior: 'smooth', block: 'center' });
        tile.classList.remove('is-highlighted');

        // Restart the highlight animation
        void tile.offsetWidth; // eslint-disable-line no-void
        tile.classList.add('is-highlighted');
    }

    function handleThreadClick(e) {
        const marker = e.target.closest('.ai-search__citation-marker');
        if (marker) highlightReference(marker.dataset.reference);
    }

    async function handleSubmit(e) {
        e.preventDefault();

        const q = question.value.trim();
        if (q === '' || busy) return;

        busy = true;
        submit.disabled = true;
        question.value = '';

        addMessage('user', { text: q });
        const pending = addMessage('assistant', { text: 'Thinking\u2026', pending: true });

        try {
            const res = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken,
                },
                body: JSON.stringify({ question: q }),
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.error || data.message || `Request failed (${res.status})`);
            }

            exchangeCount += 1;
            pending.remove();

            const message = addMessage('assistant', { markdown: data.answer });
            linkCitationMarkers(
                message.querySelector('.ai-search__message-content'),
                exchangeCount,
                data.citations.length,
            );
            addReferenceGroup(data.citations, exchangeCount, q);
        } catch (err) {
            pending.remove();
            const errorMessage = addMessage('assistant', { text: err.message });
            errorMessage.classList.add('is-error');
        } finally {
            busy = false;
            submit.disabled = false;
            question.focus();
        }
    }

    function handleQuestionKeydown(e) {
        // Enter submits; shift+enter inserts a newline
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            form.requestSubmit();
        }
    }

    // Create event listeners
    launcher.addEventListener('click', () => events.emit(actions.openAiSearch));
    close.addEventListener('click', () => events.emit(actions.closeAiSearch));
    scrim.addEventListener('click', () => events.emit(actions.closeAiSearch));
    form.addEventListener('submit', handleSubmit);
    question.addEventListener('keydown', handleQuestionKeydown);
    thread.addEventListener('click', handleThreadClick);

    events.on(actions.openAiSearch, handleOpen);
    events.on(actions.closeAiSearch, handleClose);
    events.on(actions.askAiSearch, handleAsk);
}
